import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { Platform, View } from 'react-native';
import styled from 'styled-components/native';
import { match } from 'ts-pattern';
import { SccButton, SccPrimaryButton, SccSecondaryButton, } from '@/components/atoms';
import TextArea from '@/components/form/TextArea';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { ClosedSubTypeDto, ElevatorCorrectionTargetDto, InaccurateInfoCategoryDto, } from '@/generated-sources/openapi';
import { doorTypeMap } from '@/constant/options';
import BottomSheet from '@/modals/BottomSheet';
/** 실제 데이터 기반으로 동적 카테고리 라벨을 생성한다. 질문이 적은 카테고리는 실제 값을 보여준다. */
function getCategoryLabel(category, snapshot, hasBa) {
    switch (category) {
        case InaccurateInfoCategoryDto.PlaceEntrance:
            return hasBa
                ? '계단 / 경사로 / 문 방향이 잘못됐어요'
                : '입구 계단 / 경사로 / 문 방향이 잘못됐어요';
        case InaccurateInfoCategoryDto.BuildingEntrance:
            return '건물 입구 계단 / 경사로 / 문이 잘못됐어요';
        case InaccurateInfoCategoryDto.Floor: {
            // PDP의 getFloorAccessibility title과 일치시킨다
            const floors = snapshot === null || snapshot === void 0 ? void 0 : snapshot.floors;
            const isStandalone = (snapshot === null || snapshot === void 0 ? void 0 : snapshot.isStandaloneBuilding) === true;
            if (!floors || floors.length === 0) {
                return '층 정보가 잘못됐어요';
            }
            const isSingle = floors.length === 1;
            if (isSingle && floors[0] === 1) {
                return isStandalone ? '단독 1층 건물이 아니에요' : '1층이 아니에요';
            }
            if (isSingle) {
                const name = floors[0] < 0 ? `지하 ${-floors[0]}층` : `${floors[0]}층`;
                return `${name}이 아니에요`;
            }
            // 여러층
            return isStandalone
                ? '단독 건물 여러층이 아니에요'
                : '1층을 포함한 여러층이 아니에요';
        }
        case InaccurateInfoCategoryDto.DoorType: {
            // PDP와 동일하게 doorTypeMap join으로 표시
            const doorTypes = snapshot === null || snapshot === void 0 ? void 0 : snapshot.entranceDoorTypes;
            if (doorTypes && doorTypes.length > 0 && doorTypes.length <= 2) {
                const label = doorTypes.map(d => doorTypeMap[d]).join(', ');
                return `${label}이 아니에요`;
            }
            return '출입문 종류가 잘못됐어요';
        }
        case InaccurateInfoCategoryDto.Elevator:
            return '엘리베이터 정보가 잘못됐어요';
        case InaccurateInfoCategoryDto.AccessLevel: {
            const score = snapshot === null || snapshot === void 0 ? void 0 : snapshot.accessibilityScore;
            if (score !== undefined && score !== null) {
                return `접근레벨 ${Math.floor(score)}이 아니에요`;
            }
            return '접근 레벨이 잘못됐어요';
        }
        case InaccurateInfoCategoryDto.Photo:
            return '사진이 잘못됐어요';
        case InaccurateInfoCategoryDto.Other:
            return '기타 정보가 잘못됐어요';
        default: {
            const _exhaustiveCheck = category;
            return _exhaustiveCheck;
        }
    }
}
const CLOSED_SUB_TYPE_LABELS = {
    [ClosedSubTypeDto.PermanentlyClosed]: '폐업했어요',
    [ClosedSubTypeDto.ReplacedByOther]: '다른 가게로 바뀌었어요',
    [ClosedSubTypeDto.Other]: '기타',
};
const PlaceDetailNegativeFeedbackBottomSheet = ({ isVisible, placeId, hasBuildingAccessibility = false, hasPlaceAccessibility = false, hasPhoto = false, hasDoorType = false, elevatorTargets = [], placeAccessibilitySnapshot, onPressCloseButton, onPressSubmitButton, onPressNavigateToCorrection, onPressSubmitClosed, }) => {
    const [step, setStep] = useState('reason');
    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedElevatorTarget, setSelectedElevatorTarget] = useState(null);
    const [selectedClosedSubType, setSelectedClosedSubType] = useState(null);
    const [text, setText] = useState(null);
    const [closedDetail, setClosedDetail] = useState(null);
    const reasons = [
        'INACCURATE_INFO',
        'CLOSED',
        'BAD_USER',
    ];
    const onClear = useCallback(() => {
        setSelectedReason(null);
        setStep('reason');
        setText(null);
        setSelectedCategory(null);
        setSelectedElevatorTarget(null);
        setSelectedClosedSubType(null);
        setClosedDetail(null);
    }, []);
    const handleClose = useCallback(() => {
        onClear();
        onPressCloseButton();
    }, [onClear, onPressCloseButton]);
    const handleBack = useCallback(() => {
        setStep('reason');
        setSelectedCategory(null);
        setSelectedElevatorTarget(null);
        setSelectedClosedSubType(null);
        setClosedDetail(null);
        setText(null);
    }, []);
    const getTitle = () => {
        switch (step) {
            case 'reason':
                return '어떤 문제가 있나요?';
            case 'inaccurateCategory':
                return '어떤 정보가 틀렸나요?';
            case 'closedSubType':
                return '어떤 상황인가요?';
            case 'text':
                return '차단 사유를 알려주세요.';
            default: {
                const _exhaustiveCheck = step;
                return _exhaustiveCheck;
            }
        }
    };
    const getSubmitButtonText = () => {
        switch (step) {
            case 'reason':
                return '다음';
            case 'inaccurateCategory':
                return '다음';
            case 'closedSubType':
                return selectedClosedSubType === ClosedSubTypeDto.ReplacedByOther
                    ? '폐업 처리 및 제출'
                    : '제출하기';
            case 'text':
                return '제출하기';
            default: {
                const _exhaustiveCheck = step;
                return _exhaustiveCheck;
            }
        }
    };
    const isSubmitDisabled = () => {
        switch (step) {
            case 'reason':
                return selectedReason === null;
            case 'inaccurateCategory':
                // Elevator category requires an elevator target to be selected
                if (selectedCategory === InaccurateInfoCategoryDto.Elevator) {
                    return selectedElevatorTarget === null;
                }
                return selectedCategory === null;
            case 'closedSubType':
                return selectedClosedSubType === null;
            case 'text':
                return false;
            default: {
                const _exhaustiveCheck = step;
                return _exhaustiveCheck;
            }
        }
    };
    const handleSubmit = () => {
        switch (step) {
            case 'reason': {
                if (!selectedReason) {
                    return;
                }
                switch (selectedReason) {
                    case 'INACCURATE_INFO':
                        setStep('inaccurateCategory');
                        break;
                    case 'CLOSED':
                        setStep('closedSubType');
                        break;
                    case 'BAD_USER':
                        setStep('text');
                        break;
                    default: {
                        const _exhaustiveCheck = selectedReason;
                        return _exhaustiveCheck;
                    }
                }
                break;
            }
            case 'inaccurateCategory': {
                if (!selectedCategory) {
                    return;
                }
                onPressNavigateToCorrection({
                    placeId,
                    reason: 'INACCURATE_INFO',
                    inaccurateCategories: [selectedCategory],
                    elevatorTarget: selectedElevatorTarget !== null && selectedElevatorTarget !== void 0 ? selectedElevatorTarget : undefined,
                });
                break;
            }
            case 'closedSubType': {
                if (!selectedClosedSubType) {
                    return;
                }
                const subType = selectedClosedSubType;
                const detail = subType === ClosedSubTypeDto.Other
                    ? (closedDetail !== null && closedDetail !== void 0 ? closedDetail : undefined)
                    : undefined;
                onClear();
                onPressSubmitClosed({
                    placeId,
                    closedSubType: subType,
                    detail,
                });
                break;
            }
            case 'text': {
                const reason = selectedReason;
                onClear();
                if (reason) {
                    onPressSubmitButton(placeId, reason, text !== null && text !== void 0 ? text : '');
                }
                break;
            }
            default: {
                const _exhaustiveCheck = step;
                return _exhaustiveCheck;
            }
        }
    };
    return (_jsx(BottomSheet, { isVisible: isVisible, onPressBackground: handleClose, children: _jsxs(ContentsContainer, { children: [_jsx(Title, { children: getTitle() }), step === 'reason' && (_jsx(OptionSelector, { children: reasons.map((reason, index) => {
                        const isSelected = reason === selectedReason;
                        return (_jsxs(View, { children: [index > 0 && _jsx(SpaceBetweenOptions, {}), _jsx(FeedbackOptionButton, { text: match(reason)
                                        .with('INACCURATE_INFO', () => '틀린 정보가 있어요')
                                        .with('CLOSED', () => '폐점/이전된 곳이에요')
                                        .with('BAD_USER', () => '이 정복자를 차단할래요')
                                        .exhaustive(), isSelected: isSelected, onPress: () => {
                                        setSelectedReason(reason);
                                    }, elementName: "place_feedback_option", logParams: { reason } })] }, reason));
                    }) })), step === 'inaccurateCategory' && (_jsx(OptionSelector, { children: (() => {
                        const items = [];
                        // 신고 빈도순으로 정렬 (데이터 기반)
                        const CATEGORY_ORDER = [
                            InaccurateInfoCategoryDto.PlaceEntrance,
                            InaccurateInfoCategoryDto.Floor,
                            InaccurateInfoCategoryDto.Photo,
                            InaccurateInfoCategoryDto.DoorType,
                            InaccurateInfoCategoryDto.BuildingEntrance,
                            InaccurateInfoCategoryDto.Elevator,
                            InaccurateInfoCategoryDto.AccessLevel,
                            InaccurateInfoCategoryDto.Other,
                        ];
                        CATEGORY_ORDER.forEach(cat => {
                            // PDP 홈탭 노출 조건과 1:1 대응하여 실제로 렌더되는 정보만 신고 옵션으로 제공.
                            if (cat === InaccurateInfoCategoryDto.PlaceEntrance &&
                                !hasPlaceAccessibility) {
                                return;
                            }
                            if (cat === InaccurateInfoCategoryDto.Photo && !hasPhoto) {
                                return;
                            }
                            if (cat === InaccurateInfoCategoryDto.DoorType &&
                                !hasDoorType) {
                                return;
                            }
                            if (cat === InaccurateInfoCategoryDto.BuildingEntrance &&
                                !hasBuildingAccessibility) {
                                return;
                            }
                            if (cat === InaccurateInfoCategoryDto.Elevator) {
                                // Render one button per elevator target
                                elevatorTargets.forEach(target => {
                                    items.push({
                                        key: `${cat}_${target}`,
                                        label: target === ElevatorCorrectionTargetDto.Pa
                                            ? '매장 엘리베이터 정보가 잘못됐어요'
                                            : '건물 엘리베이터 정보가 잘못됐어요',
                                        category: cat,
                                        elevatorTarget: target,
                                    });
                                });
                                return;
                            }
                            items.push({
                                key: cat,
                                label: getCategoryLabel(cat, placeAccessibilitySnapshot, hasBuildingAccessibility),
                                category: cat,
                            });
                        });
                        return items.map((item, index) => {
                            const isSelected = item.category === selectedCategory &&
                                (item.elevatorTarget != null
                                    ? item.elevatorTarget === selectedElevatorTarget
                                    : selectedElevatorTarget === null ||
                                        selectedCategory !== InaccurateInfoCategoryDto.Elevator);
                            return (_jsxs(View, { children: [index > 0 && _jsx(SpaceBetweenOptions, {}), _jsx(FeedbackOptionButton, { text: item.label, isSelected: isSelected, onPress: () => {
                                            var _a;
                                            setSelectedCategory(item.category);
                                            setSelectedElevatorTarget((_a = item.elevatorTarget) !== null && _a !== void 0 ? _a : null);
                                        }, elementName: "place_feedback_inaccurate_category", logParams: {
                                            category: item.category,
                                            elevatorTarget: item.elevatorTarget,
                                        } })] }, item.key));
                        });
                    })() })), step === 'closedSubType' && (_jsxs(OptionSelector, { children: [Object.values(ClosedSubTypeDto).map((subType, index) => {
                            const isSelected = subType === selectedClosedSubType;
                            return (_jsxs(View, { children: [index > 0 && _jsx(SpaceBetweenOptions, {}), _jsx(FeedbackOptionButton, { text: CLOSED_SUB_TYPE_LABELS[subType], isSelected: isSelected, onPress: () => {
                                            setSelectedClosedSubType(subType);
                                        }, elementName: "place_feedback_closed_sub_type", logParams: { subType } })] }, subType));
                        }), selectedClosedSubType === ClosedSubTypeDto.ReplacedByOther && (_jsx(GuideCard, { children: _jsx(GuideText, { children: "\uAE30\uC874 \uC7A5\uC18C\uB97C \uD3D0\uC5C5 \uCC98\uB9AC\uD558\uACE0, \uC0C8 \uAC00\uAC8C\uC758 \uC811\uADFC\uC131 \uC815\uBCF4\uB97C \uB4F1\uB85D\uD574\uC8FC\uC138\uC694!" }) })), selectedClosedSubType === ClosedSubTypeDto.Other && (_jsx(ClosedDetailTextArea, { placeholder: "\uAD6C\uCCB4\uC801\uC778 \uC0C1\uD669\uC744 \uC54C\uB824\uC8FC\uC138\uC694.", value: closedDetail !== null && closedDetail !== void 0 ? closedDetail : '', onChangeText: setClosedDetail, style: {
                                minHeight: Platform.OS === 'android' ? 80 : undefined,
                            } }))] })), step === 'text' && (_jsx(TextArea, { placeholder: `이 정복자를 차단하고 싶은 이유를 알려주세요.`, value: text !== null && text !== void 0 ? text : '', onChangeText: setText, style: {
                        minHeight: Platform.OS === 'android' ? 120 : undefined,
                    } })), _jsxs(ButtonContainer, { children: [_jsx(CloseButton, { text: step === 'reason' ? '닫기' : '이전', onPress: step === 'reason' ? handleClose : handleBack, elementName: step === 'reason' ? 'place_feedback_close' : 'place_feedback_back' }), _jsx(SpaceBetweenButtons, {}), _jsx(SubmitButton, { text: getSubmitButtonText(), isDisabled: isSubmitDisabled(), onPress: handleSubmit, elementName: "place_feedback_submit" })] })] }) }));
};
export default PlaceDetailNegativeFeedbackBottomSheet;
function FeedbackOptionButton({ text, isSelected, onPress, elementName, logParams, }) {
    return (_jsx(SccButton, { text: text, textColor: isSelected ? 'brand50' : 'gray80v2', buttonColor: isSelected ? 'brand5' : 'white', borderColor: isSelected ? 'brand40' : 'gray20v2', fontFamily: font.pretendardMedium, fontSize: 16, style: { borderRadius: 14 }, onPress: onPress, elementName: elementName, logParams: logParams }));
}
const ContentsContainer = styled.View `
  flex-direction: column;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-color: ${color.white};
  padding-horizontal: 24px;
`;
const Title = styled.Text `
  color: ${color.black};
  font-size: 20px;
  font-family: ${font.pretendardBold};
  margin-top: 28px;
  margin-bottom: 30px;
`;
const OptionSelector = styled.View ``;
const SpaceBetweenOptions = styled.View `
  height: 10px;
`;
const ButtonContainer = styled.View `
  height: 96px;
  flex-direction: row;
  align-items: center;
`;
const SpaceBetweenButtons = styled.View `
  width: 10px;
`;
const CloseButton = styled(SccSecondaryButton) `
  flex: 1;
`;
const SubmitButton = styled(SccPrimaryButton) `
  flex: 2;
`;
const ClosedDetailTextArea = styled(TextArea) `
  margin-top: 12px;
`;
const GuideCard = styled.View `
  margin-top: 12px;
  padding: 14px 16px;
  background-color: ${color.gray10};
  border-radius: 8px;
`;
const GuideText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  color: ${color.gray70};
  line-height: 20px;
`;
